<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDashboardNotifications } from '@/composables/useDashboardNotifications'
import {
  CLAIM_FUNNEL_EVENTS,
  trackClaimFunnel,
} from '@/helpers/claimTaxBack/funnel'
import claimCheckIllustration from '@/assets/images/claim-tax-back-check.svg'

/**
 * Claim tax back discovery in the home notifications slot. Free donors see
 * intrigue until they run the checker or dismiss. Paid donors see a no-price
 * prompt until they first open the claim page or dismiss. Dismissal is
 * per-donor and survives refresh.
 */
const { t } = useI18n()
const router = useRouter()
const {
  showClaimTaxBackNotification,
  showClaimTaxBackPaidNotification,
  dismissClaimTaxBackPrompt,
} = useDashboardNotifications()

const isPaidPrompt = computed(() => showClaimTaxBackPaidNotification.value)

const isVisible = computed(
  () =>
    showClaimTaxBackNotification.value || showClaimTaxBackPaidNotification.value
)

const copyRoot = computed(() =>
  isPaidPrompt.value
    ? 'Dashboard.Notifications.ClaimTaxBack.Paid'
    : 'Dashboard.Notifications.ClaimTaxBack'
)

const onCheck = () => {
  router.push({ name: 'ClaimTaxBack' }).catch(() => {})
}

const onDismiss = () => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.PromptDismiss, {
    prompt: isPaidPrompt.value ? 'paid' : 'free',
  })
  dismissClaimTaxBackPrompt()
}

// Profile can land after mount; fire once when the card actually appears.
let promptViewed = false
watch(
  isVisible,
  (visible) => {
    if (!visible || promptViewed) return
    promptViewed = true
    trackClaimFunnel(CLAIM_FUNNEL_EVENTS.PromptView, {
      prompt: isPaidPrompt.value ? 'paid' : 'free',
    })
  },
  { immediate: true }
)
</script>

<template>
  <section
    v-if="isVisible"
    class="sa-notification-banner sa-notification-banner--claim"
    aria-labelledby="claim-tax-back-notification-title"
  >
    <div class="sa-notification-banner__icon" aria-hidden="true">
      <img
        :src="claimCheckIllustration"
        class="sa-notification-banner__illustration"
        alt=""
        width="88"
        height="62"
      />
    </div>
    <div class="sa-notification-banner__body">
      <h3
        id="claim-tax-back-notification-title"
        class="sa-notification-banner__title"
      >
        {{ t(`${copyRoot}.Title`) }}
      </h3>
      <p class="sa-notification-banner__text">
        {{ t(`${copyRoot}.Body`) }}
      </p>
      <div class="sa-notification-banner__actions">
        <button
          type="button"
          class="sa-btn-primary sa-btn-primary--compact"
          @click="onCheck"
        >
          {{ t(`${copyRoot}.Cta`) }}
        </button>
      </div>
    </div>
    <button
      type="button"
      class="sa-notification-banner__close"
      :aria-label="t('Dashboard.Notifications.ClaimTaxBack.DismissAria')"
      @click="onDismiss"
    >
      ✕
    </button>
  </section>
</template>
